import React, { useEffect } from "react";
import { Divider, IconButton, Tooltip } from "@mui/material";
import {
  globalMenu,
  footerMsgStore,
  onSearchStore,
  global,
  useMenuStore,
  menuInitStore,
} from "@shared/libs/Global";
import { showPopupModal } from "@widgets/CommonModal";
import ExcelDialog from "@widgets/commonpopup/ExcelDialog";
import { showDialog } from "@widgets/CommonDialog";
import { callApi, fnGetDataSet } from "@shared/api/restApi";
import ReportPopup from "@widgets/commonpopup/ReportPopup";
import ReportViewer from "@widgets/commonpopup/ReportViewer";
import axios from "axios";
import { excelMenu } from "@shared/libs/ExcelMenu";
import { ShareButton } from "@shared/components/screenShare/ShareButton";

interface Props {
  index: number;
  componentRefs: any[];
  menuData: any[];
  isLoading: (cd_menu: string, state: boolean) => void;
  tabTitle: (cd_menu: string, state: boolean) => void;
  loadingMenu: Map<string, boolean>;
}

export default function TabToolbar({
  index,
  componentRefs,
  menuData,
  isLoading,
  tabTitle,
  loadingMenu,
}: Props): React.ReactElement {
  const { setActiveMenuInfo } = globalMenu();
  const { handleFooterMsg } = footerMsgStore();
  const { setOnSearch, runOnSearch } = onSearchStore();
  const { menuItems, setMenuItems } = useMenuStore();
  const { userInfo, bookmark, setBookmark } = global.getState();
  // 단위화면이 useImperativeHandle로 노출한 함수만 활성화한다.
  // ref.current가 채워지면 MainLayout이 리렌더를 일으키므로 렌더 시점에 바로 읽으면 된다.
  const componentHandle = componentRefs?.[index]?.current;
  const hasHandle = (...names: string[]) =>
    names.some((name) => typeof componentHandle?.[name] === "function");

  // 화면 초기화(getInitForm 등)가 끝나기 전에는 버튼을 잠근다.
  // 조회조건이 아직 세팅되지 않은 상태로 조회/저장이 나가면 서버 에러가 난다.
  const isInitializing = menuInitStore((state) => {
    const cd_menu = menuData?.[index]?.cd_menu;
    return cd_menu ? (state.initializingMenus[cd_menu] || 0) > 0 : false;
  });

  // 진행 중인 작업과 다른 작업이 동시에 실행되는 것을 막는다(메뉴별 잠금).
  const isBusy = () => {
    const cd_menu = menuData?.[index]?.cd_menu;
    if (!cd_menu) return false;
    if (loadingMenu?.has(cd_menu)) return true;
    return (menuInitStore.getState().initializingMenus[cd_menu] || 0) > 0;
  };

  const canSearch = !isInitializing && hasHandle("onSearch");
  const canInsert = !isInitializing && hasHandle("onInsert");
  const canDelete = !isInitializing && hasHandle("onDelete");
  const canSave = !isInitializing && hasHandle("onSave");
  const canExcel =
    !isInitializing && hasHandle("onExcelUpload", "onExcelDownLoad");
  const canPrint = !isInitializing && hasHandle("onPrint");

  useEffect(() => {
    if (menuData[index] != null) {
      setActiveMenuInfo({
        cd_menu: menuData[index].cd_menu,
        nm_menu: menuData[index].nm_menu,
        cd_module: menuData[index].cd_module,
      });
      setOnSearch({
        index: index,
        componentRefs: componentRefs,
        menuData: menuData,
        isLoading: isLoading,
        tabTitle: tabTitle,
      });
    } else {
      /* 열린 탭이 없다 — 닫기 직전 화면 정보를 비운다(처음 띄웠을 때와 같은 값).
         남겨 두면 챗봇이 닫힌 화면을 "지금 화면" 으로 보내 조회가 "검색 패널이 준비되지 않았습니다" 로 멈추고,
         하단 메시지 · 대화방 시작 메뉴에 닫은 메뉴가 남는다 (2026-10-13) */
      setActiveMenuInfo({ cd_menu: "" });
      setOnSearch({
        index: index,
        componentRefs: componentRefs,
        menuData: menuData,
        isLoading: isLoading,
        tabTitle: tabTitle,
      });
    }
  }, [componentRefs, menuData, index]);

  const onSearch = async () => {
    if (isBusy()) return;
    await runOnSearch();
  };

  const onInsert = () => {
    if (isBusy()) return;
    if (typeof componentRefs[index]?.current?.onInsert === "function") {
      componentRefs[index].current.onInsert();
    }
  };

  // 단위화면 함수가 예외를 던져도 spinner는 반드시 내려야 한다(finally).
  // 안 내리면 로딩 상태가 메뉴코드에 남아 화면을 닫고 다시 열어도 계속 돈다.
  const onDelete = async () => {
    if (isBusy()) return;
    if (typeof componentRefs[index]?.current?.onDelete === "function") {
      const cd_menu = menuData[index].cd_menu;
      isLoading(cd_menu, true);
      tabTitle(cd_menu, true);
      try {
        await componentRefs[index].current.onDelete();
      } finally {
        isLoading(cd_menu, false);
        tabTitle(cd_menu, false);
      }
    }
  };

  const onSave = async () => {
    if (isBusy()) return;
    if (typeof componentRefs[index]?.current?.onSave === "function") {
      const cd_menu = menuData[index].cd_menu;
      isLoading(cd_menu, true);
      tabTitle(cd_menu, true);
      try {
        await componentRefs[index].current.onSave();
      } finally {
        isLoading(cd_menu, false);
        tabTitle(cd_menu, false);
      }
    }
  };

  const onPrint = async () => {
    if (isBusy()) return;
    try {
      if (typeof componentRefs[index]?.current?.onPrint === "function") {
        const params = await componentRefs[index].current.onPrint();
        if (!params) return;
        const data: any = await fnGetDataSet({
          id: "POPUP_REPORT_01",
          params: { cd_menu: globalMenu.getState().activeMenuInfo.cd_menu },
        });
        if (data.length > 0) {
          if (data[0].length > 0) {
            let result;
            if (data[0].length > 1) {
              let defaultMailAddress = "";
              if (params.dc_mail_send_address) {
                defaultMailAddress = params.dc_mail_send_address;
              }
              result = await showPopupModal({
                title: "보고서도움창",
                children: (
                  <ReportPopup
                    dataSource={data[0]}
                    defaultMailAddress={defaultMailAddress}
                  />
                ),
                width: "500px",
                height: "550px",
                // 26.08.21 ysw 보고서를 선택하지 않고 확인을 눌러도
                // 경고 없이 취소와 동일하게 닫히도록 한다.
                checkData: false,
              });
            } else {
              result = data[0];
            }

            if (Array.isArray(result) && result[0]?.cd_report) {
              let dataJson: any = {};
              if (result[0].cd_querys) {
                const queryParams = {
                  ids: result[0].cd_querys,
                  params: params,
                };
                const response = await axios.post(
                  "/snotes/getReportDataSet",
                  queryParams,
                  {
                    headers: {
                      "X-TENANT-ID":
                        global.getState().userInfo.tenant_id || "DEFAULT",
                    },
                  }
                );
                dataJson = response.data;
              }
              await showPopupModal({
                title: "",
                children: (
                  <ReportViewer
                    reportName={result[0].cd_report}
                    dataJson={dataJson}
                  />
                ),
                width: "1400px",
                height: "1000px",
                // 26.08.21 ysw 출력물 미리보기는 선택할 항목이 없는 화면이므로
                // 확인을 눌러도 경고 없이 취소와 동일하게 닫히도록 한다.
                checkData: false,
              });
            }
          }
        }
      }
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onExcel = async () => {
    if (isBusy()) return;
    if (
      typeof componentRefs[index]?.current?.onExcelUpload === "function" ||
      typeof componentRefs[index]?.current?.onExcelDownLoad === "function"
    ) {
      const component = <ExcelDialog />;
      const figure = await showPopupModal({
        title: "Excel ",
        children: component,
        width: "360px",
        height: "200px",
      });

      let result;

      if (figure) {
        if (figure == "download") {
          if (
            typeof componentRefs[index]?.current?.onExcelDownLoad === "function"
          )
            result = await componentRefs[index].current.onExcelDownLoad();
          else {
            showDialog("alter", "Excel Upload 양식을 확인해 주세요.");
          }
        } else if (figure == "upload") {
          if (
            typeof componentRefs[index]?.current?.onExcelUpload === "function"
          ) {
            result = await componentRefs[index].current.onExcelUpload();
          } else {
            showDialog("alter", "Excel Upload 기능을 확인해 주세요.");
          }
        }
      }

      handleFooterMsg(menuData[index].cd_menu, result);
    }
  };

  const onBookmark = async (menuInfo: any) => {
    const flag = bookmark[menuInfo.cd_menu] === true;
    if (flag) {
      // 북마크 삭제
      const params = {
        cd_firm: userInfo.cd_firm,
        cd_menu: menuInfo.cd_menu,
        cd_user: userInfo.cd_user,
      };
      const result = await callApi("delFav", params);

      if (result.status) {
        // 상태 업데이트
        const updated = { ...bookmark };
        delete updated[menuInfo.cd_menu];
        setBookmark(updated);

        const updatedItems = menuItems.filter(
          (item) =>
            item.cd_menu !== menuInfo.cd_menu ||
            item.cd_menu_parent !== "SN_FAV"
        );
        setMenuItems(updatedItems);
      }
    } else {
      // 북마크 추가
      const params = {
        cd_firm: userInfo.cd_firm,
        cd_menu: menuInfo.cd_menu,
        nm_menu: menuInfo.nm_menu,
        cd_user: userInfo.cd_user,
        no_pos: menuInfo.no_pos,
      };
      const result = await callApi("addFav", params);

      if (result.status) {
        // 상태 업데이트
        const updated = { ...bookmark, [menuInfo.cd_menu]: true };
        setBookmark(updated);

        // leftMenu 변경
        const newMenu = [
          {
            cd_menu: menuInfo.cd_menu,
            nm_menu: menuInfo.nm_menu,
            nm_menu_origin: menuInfo.nm_menu_origin,
            cd_module: menuInfo.cd_module,
            no_pos: "000",
            nm_file: menuInfo.nm_file,
            fg_menu: menuInfo.fg_menu,
            no_seq: 0,
            no_level: 2,
            cd_menu_parent: "SN_FAV",
            fg_select: "Y",
            fg_insert: "Y",
            fg_update: "Y",
            fg_delete: "Y",
            fg_excel: "Y",
            fg_print: "Y",
            yn_end: "Y",
            path_arr: "SN_FAV," + menuInfo.cd_menu,
            path_origin: menuInfo.path_origin,
            // nm_path: "Quick Menu",
          },
        ];
        const transformedData = newMenu.map((item: any) => ({
          ...item,
          path_arr: item.path_arr.split(","),
          path_origin: item.path_origin ? item.path_origin : [],
        }));
        setMenuItems([...menuItems, ...transformedData]);
      }
    }
  };

  return (
    <div
      style={{
        marginRight: 16,
        display: "flex",
        gap: "15px",
        justifyContent: "flex-end",
      }}
    >
      {menuData != null && menuData[index] != null && canSearch && (
        <Tooltip title="Search">
          <IconButton
            onClick={onSearch}
            sx={{
              backgroundImage: "url(/images/search_off.svg)", // 기본 상태 이미지
              backgroundSize: "cover", // 이미지 크기 맞추기
              width: 25, // 버튼 크기
              height: 25, // 버튼 크기
              "&:hover": {
                backgroundImage: "url(/images/search_on.svg)", // hover 시 이미지
                transform: "scale(1.2)", // hover 시 크기 확대
                transition: "all 0.3s ease", // 부드러운 애니메이션
              },
            }}
          ></IconButton>
        </Tooltip>
      )}
      {menuData != null && menuData[index] != null && !canSearch && (
        <IconButton
          disableRipple
          sx={{
            backgroundImage: "url(/images/search_off.svg)", // 기본 상태 이미지
            backgroundSize: "cover", // 이미지 크기 맞추기
            width: 25, // 버튼 크기
            height: 25, // 버튼 크기
            opacity: 0.35, // 비활성 표시
            cursor: "default",
          }}
        ></IconButton>
      )}
      {menuData != null &&
        menuData[index] != null &&
        canInsert &&
        menuData[index].fg_insert == "Y" && (
          <Tooltip title="Insert">
            <IconButton
              onClick={onInsert}
              sx={{
                backgroundImage: "url(/images/new_off.svg)", // 기본 상태 이미지
                backgroundSize: "cover", // 이미지 크기 맞추기
                width: 25, // 버튼 크기
                height: 25, // 버튼 크기
                "&:hover": {
                  backgroundImage: "url(/images/new_on.svg)", // hover 시 이미지
                  transform: "scale(1.2)", // hover 시 크기 확대
                  transition: "all 0.3s ease", // 부드러운 애니메이션
                },
              }}
            ></IconButton>
          </Tooltip>
        )}
      {menuData != null &&
        menuData[index] != null &&
        (!canInsert || menuData[index].fg_insert == "N") && (
          <IconButton
            disableRipple
            sx={{
              backgroundImage: "url(/images/new_off.svg)", // 기본 상태 이미지
              backgroundSize: "cover", // 이미지 크기 맞추기
              width: 25, // 버튼 크기
              height: 25, // 버튼 크기
              opacity: 0.35, // 비활성 표시
              cursor: "default",
            }}
          ></IconButton>
        )}

      {menuData != null &&
        menuData[index] != null &&
        canSave &&
        menuData[index].fg_update == "Y" && (
          <Tooltip title="Save">
            <IconButton
              onClick={onSave}
              sx={{
                backgroundImage: "url(/images/save_off.svg)", // 기본 상태 이미지
                backgroundSize: "cover", // 이미지 크기 맞추기
                width: 25, // 버튼 크기
                height: 25, // 버튼 크기
                "&:hover": {
                  backgroundImage: "url(/images/save_on.svg)", // hover 시 이미지
                  transform: "scale(1.2)", // hover 시 크기 확대
                  transition: "all 0.3s ease", // 부드러운 애니메이션
                },
              }}
            ></IconButton>
          </Tooltip>
        )}
      {menuData != null &&
        menuData[index] != null &&
        (!canSave || menuData[index].fg_update == "N") && (
          <IconButton
            disableRipple
            sx={{
              backgroundImage: "url(/images/save_off.svg)", // 기본 상태 이미지
              backgroundSize: "cover", // 이미지 크기 맞추기
              width: 25, // 버튼 크기
              height: 25, // 버튼 크기
              opacity: 0.35, // 비활성 표시
              cursor: "default",
            }}
          ></IconButton>
        )}

      {menuData?.[index] &&
        excelMenu.includes(menuData[index].cd_menu) &&
        canExcel && (
          <Tooltip title="Excel">
            <IconButton
              onClick={onExcel}
              sx={{
                backgroundImage: "url(/images/exel_off.svg)", // 기본 상태 이미지
                backgroundSize: "cover", // 이미지 크기 맞추기
                width: 25, // 버튼 크기
                height: 25, // 버튼 크기
                "&:hover": {
                  backgroundImage: "url(/images/exel_on.svg)", // hover 시 이미지
                  transform: "scale(1.2)", // hover 시 크기 확대
                  transition: "all 0.3s ease", // 부드러운 애니메이션
                },
              }}
            ></IconButton>
          </Tooltip>
        )}
      {menuData?.[index] &&
        excelMenu.includes(menuData[index].cd_menu) &&
        !canExcel && (
          <IconButton
            disableRipple
            sx={{
              backgroundImage: "url(/images/exel_off.svg)", // 기본 상태 이미지
              backgroundSize: "cover", // 이미지 크기 맞추기
              width: 25, // 버튼 크기
              height: 25, // 버튼 크기
              opacity: 0.35, // 비활성 표시
              cursor: "default",
            }}
          ></IconButton>
        )}

      {menuData != null &&
        menuData[index] != null &&
        canPrint &&
        menuData[index].fg_print == "Y" && (
          <Tooltip title="Print">
            <IconButton
              onClick={onPrint}
              sx={{
                backgroundImage: "url(/images/print_off.svg)", // 기본 상태 이미지
                backgroundSize: "cover", // 이미지 크기 맞추기
                width: 25, // 버튼 크기
                height: 25, // 버튼 크기
                "&:hover": {
                  backgroundImage: "url(/images/print_on.svg)", // hover 시 이미지
                  transform: "scale(1.2)", // hover 시 크기 확대
                  transition: "all 0.3s ease", // 부드러운 애니메이션
                },
              }}
            ></IconButton>
          </Tooltip>
        )}
      {menuData != null &&
        menuData[index] != null &&
        (!canPrint || menuData[index].fg_print == "N") && (
          <IconButton
            disableRipple
            sx={{
              backgroundImage: "url(/images/print_off.svg)", // 기본 상태 이미지
              backgroundSize: "cover", // 이미지 크기 맞추기
              width: 25, // 버튼 크기
              height: 25, // 버튼 크기
              opacity: 0.35, // 비활성 표시
              cursor: "default",
            }}
          ></IconButton>
        )}

      {menuData != null &&
        menuData[index] != null &&
        canDelete &&
        menuData[index].fg_delete == "Y" && (
          <Tooltip title="Delete">
            <IconButton
              onClick={onDelete}
              sx={{
                marginLeft: "15px", // 앞 버튼과 간격 두 배(기본 gap 15px + 15px)
                backgroundImage: "url(/images/delete_off.svg)", // 기본 상태 이미지
                backgroundSize: "cover", // 이미지 크기 맞추기
                width: 25, // 버튼 크기
                height: 25, // 버튼 크기
                "&:hover": {
                  backgroundImage: "url(/images/delete_on.svg)", // hover 시 이미지
                  transform: "scale(1.2)", // hover 시 크기 확대
                  transition: "all 0.3s ease", // 부드러운 애니메이션
                },
              }}
            />
          </Tooltip>
        )}
      {menuData != null &&
        menuData[index] != null &&
        (!canDelete || menuData[index].fg_delete == "N") && (
          <IconButton
            disableRipple
            sx={{
              marginLeft: "15px", // 앞 버튼과 간격 두 배(기본 gap 15px + 15px)
              backgroundImage: "url(/images/delete_off.svg)", // 기본 상태 이미지
              backgroundSize: "cover", // 이미지 크기 맞추기
              width: 25, // 버튼 크기
              height: 25, // 버튼 크기
              opacity: 0.35, // 비활성 표시
              cursor: "default",
            }}
          />
        )}

      {menuData != null && menuData[index] != null && (
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: "rgb(186, 191, 199)", my: 0.25 }}
        />
      )}

      {menuData != null && menuData[index] != null && (
        <ShareButton
          menu={menuData[index]}
          componentRef={componentRefs?.[index]}
        />
      )}

      {menuData != null && menuData[index] != null && (
        <Tooltip title="Quick Menu">
          <IconButton
            onClick={() => onBookmark(menuData[index])}
            sx={{
              backgroundImage: `url(/images/${
                bookmark[menuData[index].cd_menu]
                  ? "bookmark_on"
                  : "bookmark_off"
              }.svg)`,
              backgroundSize: "cover",
              width: 25,
              height: 25,
              "&:hover": {
                backgroundImage: `url(/images/${
                  bookmark[menuData[index].cd_menu]
                    ? "bookmark_on"
                    : "bookmark_off"
                }.svg)`,
                transform: "scale(1.2)",
                transition: "all 0.3s ease",
              },
            }}
          ></IconButton>
        </Tooltip>
      )}
    </div>
  );
}
